"use client";

import type React from "react";
import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { cn } from "@/lib/utils";
import { CardWave, waveSeed } from "@/components/ui";
import { ROUTES } from "@/lib/constants";

/* The homepage Use Cases bento, extracted so every page's use-case section
   renders the same card: green label, headline, arrow affordance, and a
   self-running mini-demo (see ./card-visuals.tsx) at the bottom. Cards are
   rounded tiles separated by real gaps, each filled by its own animated mesh
   gradient (see @/components/ui/gradient-wave).

   One WebGL context per card is affordable here: the gradient geometry is a
   tiny vertex grid, and every surface parks its render loop while the section
   is scrolled out of view. A static CSS gradient sits underneath each canvas,
   so a card is never bare before the context initialises — or at all, if the
   browser has no WebGL to give. */

export interface UseCaseGridItem {
  id: string;
  /** Small green category label, e.g. "Web Scraping". */
  label: string;
  /** The card's headline sentence. */
  headline: string;
  /** Supporting copy under the headline; omit to keep a card label + headline + visual only. */
  description?: string;
  href?: string;
  Visual?: React.ComponentType;
  /** anchor = 2×2 bento anchor, wide = spans two columns. */
  span?: "anchor" | "wide";
}

const SPAN_CLASS: Record<NonNullable<UseCaseGridItem["span"]>, string> = {
  anchor: "lg:col-span-2 lg:row-span-2",
  wide: "lg:col-span-2",
};

interface Props {
  items: UseCaseGridItem[];
}

export default function UseCasesGrid({ items }: Props) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <div className="grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-3">
      {items.map((uc, i) => {
        const Visual = uc.Visual;
        const isSpanning = !!uc.span;

        return (
          <article
            key={uc.id}
            className={cn(
              // isolate keeps the gradient's z-0 and the content's z-10 local
              // to this tile; overflow-hidden clips the canvas to the radius
              "group bg-card border-border relative isolate flex min-w-0 flex-col overflow-hidden rounded-2xl border",
              uc.span && SPAN_CLASS[uc.span]
            )}
          >
            {/* The head of the card carries the wave and it dissolves into the
                card's own surface, leaving clean ground for the product
                mock-up below. See ui/card-wave for the mask and the flat
                first-paint/no-WebGL fallback underneath it. */}
            <CardWave seed={waveSeed(uc.id)} />
            {/* The whole card is the link — a click anywhere opens the use case.
                It rides above the gradient; only the content inside animates
                in. The mini-demo runs on its own timer (card-visuals.tsx). */}
            <Link
              href={uc.href ?? ROUTES.useCases}
              aria-label={`Explore ${uc.label}`}
              className="relative z-10 flex flex-1 flex-col outline-none"
            >
              <motion.div
                initial={prefersReducedMotion ? false : { opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={scrollRevealViewport}
                transition={{ duration: 0.45, delay: Math.min(i, 4) * 0.06 }}
                className="relative flex flex-1 flex-col p-6 sm:p-7"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-brand-green mb-1.5 text-[12px] font-semibold tracking-wide">
                      {uc.label}
                    </p>
                    <h3
                      className={cn(
                        "text-foreground font-semibold tracking-[-0.01em]",
                        isSpanning ? "text-[20px]" : "max-w-65 text-[18px]"
                      )}
                    >
                      {uc.headline}
                    </h3>
                  </div>
                  {/* Decorative affordance — the card itself is the link. Expand glyph:
                    the two corner brackets drift apart on hover so the space inside
                    the icon grows. */}
                  <span
                    aria-hidden="true"
                    className="bg-brand-green/10 text-brand-green group-hover:bg-brand-green flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-200 group-hover:text-white"
                  >
                    <svg
                      width={17}
                      height={17}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path
                        d="M13 5h6v6"
                        className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />
                      <path
                        d="M11 19H5v-6"
                        className="transition-transform duration-200 group-hover:-translate-x-0.5 group-hover:translate-y-0.5"
                      />
                    </svg>
                  </span>
                </div>

                {/* lifted off muted — muted-foreground loses contrast against
                  the gradient's brighter blooms */}
                {uc.description && (
                  <p className="text-foreground/80 mt-1.5 max-w-105 text-[14px] leading-relaxed">
                    {uc.description}
                  </p>
                )}

                {Visual && (
                  <div className="flex flex-1 flex-col justify-end">
                    <Visual />
                  </div>
                )}
              </motion.div>
            </Link>
          </article>
        );
      })}
    </div>
  );
}
